import { useCallback, useEffect, useMemo, useState } from "react";
import { ScheduleStopSettlement } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Loader } from "@design-system/Loader/Loader";
import { VStack } from "@design-system/VStack/VStack";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getApiErrorMessage } from "@api/apiError";
import { useLockDate } from "@api/lock.api";
import { getFirstUnlockedDateOnly, isLockedByDateOnly } from "@helpers/lock";
import {
    ScheduleLifecycleRequest,
    isScheduleLifecycleActionVerb,
    useDeleteScheduleApplicationMutation,
    useScheduleLifecycleActionMutation,
    useScheduleLifecycleProposal,
} from "@api/scheduleTemplate.api";
import {
    ScheduleChangeJournalEntryMode,
    ScheduleDeleteJournalEntryMode,
    ScheduleLifecycleVerb,
} from "@common/types/domains/accounting/schedule";
import { WorkspaceSettingsPages } from "@components/settings/constants";
import { SCHEDULE_OPTIONS_BACK_LABEL } from "../options/ScheduleOptionsModal";
import { useScheduleLockedPeriodsDialog } from "../useScheduleLockedPeriodsDialog";
import { useScheduleSettingsDetour } from "../useScheduleSettingsDetour";
import { SCHEDULE_LIFECYCLE_ERROR_TOASTS } from "@common/constants/domains/accounting/schedules";
import {
    SCHEDULE_LIFECYCLE_SUCCESS_TOASTS,
    buildScheduleLifecycleCopy,
} from "./scheduleLifecycleCopy";
import { ScheduleChangeModal } from "./ScheduleChangeModal";
import {
    clampToUnlocked,
    getScheduleResumeDefaultDate,
} from "./scheduleResumeDate";
import { ScheduleResumeDateField } from "./ScheduleResumeDateField";
import { ScheduleStopModal } from "./ScheduleStopModal";
import { SCHEDULE_STOP_SETTLED_TOAST } from "./scheduleStopSettlementCopy";

export type { ScheduleLifecycleRequest } from "@api/scheduleTemplate.api";

interface ScheduleLifecycleConfirmModalProps {
    request: ScheduleLifecycleRequest | null;
    onClose: () => void;
    onCompleted: (request: ScheduleLifecycleRequest) => void;
    /** Surfaces inside an overlay pass an inline dialog; the global one would replace them. */
    showLockedPeriods?: ReturnType<typeof useScheduleLockedPeriodsDialog>;
}

/**
 * Stays mounted while `request` is null so the modal can play its close
 * animation; the last request is kept until the exit finishes.
 */
export const ScheduleLifecycleConfirmModal = ({
    request,
    onClose,
    onCompleted,
    showLockedPeriods: showLockedPeriodsOverride,
}: ScheduleLifecycleConfirmModalProps) => {
    const { toast, error } = useToaster();
    const [displayedRequest, setDisplayedRequest] =
        useState<ScheduleLifecycleRequest | null>(request);
    const [resumeDate, setResumeDate] = useState<string>(
        getScheduleResumeDefaultDate,
    );
    useEffect(() => {
        if (request) {
            setDisplayedRequest(request);
            if (request.verb === ScheduleLifecycleVerb.resume) {
                setResumeDate(getScheduleResumeDefaultDate());
            }
        }
    }, [request]);

    // Keyed on the displayed request so the copy survives the exit animation.
    const proposalQuery = useScheduleLifecycleProposal(displayedRequest);
    // Unlocking from the change dialog comes back to it with fresh options.
    const { refetch: refetchProposal } = proposalQuery;
    const settingsDetour = useScheduleSettingsDetour(
        useCallback(() => void refetchProposal(), [refetchProposal]),
    );
    const lockDate = useLockDate(displayedRequest?.entityId);
    const firstUnlockedDate = getFirstUnlockedDateOnly(lockDate);
    const showLockedPeriods = useScheduleLockedPeriodsDialog(
        showLockedPeriodsOverride,
    );
    // Resume defaults to the month of the last entry still on the ledger, so
    // the schedule picks up where the pause left it.
    const lastJournalEntryDate =
        proposalQuery.data?.verb === ScheduleLifecycleVerb.resume
            ? proposalQuery.data.summary.lastJournalEntryDate
            : null;
    useEffect(() => {
        if (lastJournalEntryDate) {
            setResumeDate(
                clampToUnlocked(lastJournalEntryDate, firstUnlockedDate),
            );
        }
    }, [lastJournalEntryDate, firstUnlockedDate]);
    const actionMutation = useScheduleLifecycleActionMutation();
    const deleteMutation = useDeleteScheduleApplicationMutation();
    const copy = useMemo(
        () =>
            proposalQuery.data
                ? buildScheduleLifecycleCopy(proposalQuery.data)
                : null,
        [proposalQuery.data],
    );

    // A refusal while preparing closes this modal and drops the displayed
    // request with it, so the errored query is released at once: nothing is
    // re-toasted on a host re-render, and a resubmit previews afresh.
    const proposalError = proposalQuery.isError ? proposalQuery.error : null;
    useEffect(() => {
        if (!proposalError) {
            return;
        }
        if (!showLockedPeriods(proposalError)) {
            error(proposalError.message);
        }
        setDisplayedRequest(null);
        onClose();
    }, [proposalError, showLockedPeriods, error, onClose]);

    const handleConfirm = async ({
        journalEntryMode = ScheduleDeleteJournalEntryMode.reverse,
        changeJournalEntryMode,
        settlement,
    }: {
        journalEntryMode?: ScheduleDeleteJournalEntryMode;
        changeJournalEntryMode?: ScheduleChangeJournalEntryMode;
        settlement?: ScheduleStopSettlement;
    } = {}) => {
        if (!request) {
            return;
        }
        try {
            let completedApplicationId = request.applicationId;
            if (request.verb === ScheduleLifecycleVerb.delete) {
                await deleteMutation.mutateAsync({
                    applicationId: request.applicationId,
                    entityId: request.entityId,
                    journalEntryMode,
                });
            } else if (isScheduleLifecycleActionVerb(request.verb)) {
                const application = await actionMutation.mutateAsync({
                    applicationId: request.applicationId,
                    entityId: request.entityId,
                    verb: request.verb,
                    change: request.change,
                    effectiveDate:
                        request.verb === ScheduleLifecycleVerb.resume
                            ? resumeDate
                            : undefined,
                    journalEntryMode: changeJournalEntryMode,
                    settlement,
                });
                completedApplicationId = application.id;
            } else {
                return;
            }
            toast(
                settlement
                    ? SCHEDULE_STOP_SETTLED_TOAST
                    : SCHEDULE_LIFECYCLE_SUCCESS_TOASTS[request.verb],
                undefined,
                { variant: "success" },
            );
            onCompleted({ ...request, applicationId: completedApplicationId });
        } catch (mutationError) {
            // A locked period gets the dialog and closes this modal; anything
            // else is toasted and the modal stays open so the user can retry.
            if (showLockedPeriods(mutationError)) {
                onClose();
                return;
            }
            error(
                getApiErrorMessage(
                    mutationError,
                    SCHEDULE_LIFECYCLE_ERROR_TOASTS[request.verb],
                ),
            );
        }
    };

    if (!displayedRequest) {
        return null;
    }

    if (displayedRequest.verb === ScheduleLifecycleVerb.cancel) {
        return (
            <ScheduleStopModal
                show={request !== null}
                content={
                    proposalQuery.data && copy
                        ? {
                              title: copy.title,
                              policyLine: copy.policyLine,
                              body: copy.body,
                              summary: proposalQuery.data.summary,
                              entityId: displayedRequest.entityId,
                          }
                        : null
                }
                isSubmitting={actionMutation.isPending}
                onConfirm={(settlement) => handleConfirm({ settlement })}
                onHide={onClose}
                onExited={() => setDisplayedRequest(null)}
            />
        );
    }

    if (displayedRequest.verb === ScheduleLifecycleVerb.change) {
        return (
            <ScheduleChangeModal
                show={request !== null && !settingsDetour.isAway}
                proposal={proposalQuery.data}
                isSubmitting={actionMutation.isPending}
                onConfirm={(changeJournalEntryMode) =>
                    void handleConfirm({ changeJournalEntryMode })
                }
                onGoToLocks={() =>
                    settingsDetour.leaveFor([WorkspaceSettingsPages.LOCKS])
                }
                onHide={onClose}
                onExited={() => {
                    if (!settingsDetour.isAway) {
                        setDisplayedRequest(null);
                    }
                }}
            />
        );
    }

    const isDatedResume =
        displayedRequest.verb === ScheduleLifecycleVerb.resume &&
        proposalQuery.data?.summary.isMilestone === false;

    const renderQuestion = () => {
        if (!copy) {
            return <Loader />;
        }
        return (
            <VStack gap={12}>
                <span>{copy.policyLine}</span>
                {isDatedResume ? (
                    <ScheduleResumeDateField
                        body={copy.body}
                        value={resumeDate}
                        minDate={firstUnlockedDate}
                        onChange={setResumeDate}
                    />
                ) : (
                    <span>{copy.body}</span>
                )}
            </VStack>
        );
    };

    return (
        <ConfirmationModal
            show={request !== null}
            title={copy?.title ?? "Preparing…"}
            question={renderQuestion()}
            yes={copy?.confirmLabel ?? "Confirm"}
            yesBtnVariant={copy?.isDestructive ? "destructive" : "primary"}
            confirmDisabled={
                !copy ||
                (isDatedResume && isLockedByDateOnly(lockDate, resumeDate))
            }
            alternative={
                copy?.alternativeLabel
                    ? {
                          label: copy.alternativeLabel,
                          variant: "destructive",
                          onConfirm: () =>
                              handleConfirm({
                                  journalEntryMode:
                                      ScheduleDeleteJournalEntryMode.remove,
                              }),
                      }
                    : undefined
            }
            no={SCHEDULE_OPTIONS_BACK_LABEL}
            onReject={onClose}
            onConfirm={() => handleConfirm()}
            onHide={onClose}
            onExited={() => setDisplayedRequest(null)}
        />
    );
};
